import * as React from "react";
import { Button, Modal, FormGroup, Input, Label } from "../components/Modal";
import { DeleteManagedService, ManagedService, UpdateManagedService } from "./ManagedServiceStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SendEmail } from "../email/EmailStore";
import { ConfigFormField } from "../organizations/OrganizationManagedServices";
import { RenderScadsysInstance } from "./services/Scadsys";
import { AppWebsiteEditor } from "./services/AppWebsite";
import AzureIndex from "../azure/AzureIndex";
import { RenderScadsysAssetsInstance } from "./services/ScadsysAssets";

interface ManagedServiceInstanceProps {
    managedService: ManagedService<any>;
    onChange: Function;
}

const ManagedServiceInstance = ({ managedService, onChange }: ManagedServiceInstanceProps) => {
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [emailTestAddress, setEmailTestAddress] = React.useState("");
    const [emailTestText, setEmailTestText] = React.useState("");

    const deleteManagedService = () => {
        DeleteManagedService(managedService.organizationID, managedService.id).then(() => {
            setShowDeleteModal(false);
            onChange && onChange();
        });
    };

    const startDeleteManagedService = () => {
        setShowDeleteModal(true);
    };
    const toggleDeleteModal = () => {
        setShowDeleteModal(!showDeleteModal);
    };

    const sendTestMail = () => {
        SendEmail(emailTestAddress, "Test Mail", emailTestText).then(() => {
            setEmailTestAddress("");
            setEmailTestText("");
        });
    };

    const [limitTo, setLimitTo] = React.useState(managedService?.configuration?.limitTo);
    const [openInIframe, setOpenInIframe] = React.useState(managedService?.configuration?.openInIframe || false);

    // Azure consent URLs state
    const [loginConsentUrl, setLoginConsentUrl] = React.useState<string>("");
    const [eventsConsentUrl, setEventsConsentUrl] = React.useState<string>("");
    const [identityUsersConsentUrl, setIdentityUsersConsentUrl] = React.useState<string>("");
    const [loadingLoginConsent, setLoadingLoginConsent] = React.useState(false);
    const [loadingEventsConsent, setLoadingEventsConsent] = React.useState(false);
    const [loadingIdentityUsersConsent, setLoadingIdentityUsersConsent] = React.useState(false);

    React.useEffect(() => {
        updateManagedService();
    }, [openInIframe]);

    const updateManagedService = () => {
        if (managedService.type == 0) {
            UpdateManagedService(managedService.organizationID, { ...managedService, configuration: { ...managedService.configuration, limitTo, openInIframe } }).then(() => {});
        }

        if (managedService.type == 7) {
            UpdateManagedService(managedService.organizationID, {
                ...managedService,
                configuration: { ...managedService.configuration, defaultTemplateID, loginLinkTemplateID, transactionalAsmGroupID, vouchersAsmGroupID, marketingAsmGroupID, formsAsmGroupID },
            }).then(() => {});
        }
    };

    const [defaultTemplateID, setDefaultTemplateID] = React.useState(managedService?.configuration?.defaultTemplateID || "");
    const [loginLinkTemplateID, setLoginLinkTemplateID] = React.useState(managedService?.configuration?.loginLinkTemplateID || "");
    const [transactionalAsmGroupID, setTransactionalAsmGroupID] = React.useState(managedService?.configuration?.transactionalAsmGroupID || 0);
    const [vouchersAsmGroupID, setVouchersAsmGroupID] = React.useState(managedService?.configuration?.vouchersAsmGroupID || 0);
    const [marketingAsmGroupID, setMarketingAsmGroupID] = React.useState(managedService?.configuration?.marketingAsmGroupID || 0);
    const [formsAsmGroupID, setFormsAsmGroupID] = React.useState(managedService?.configuration?.formsAsmGroupID || 0);

    // Fetch Login Admin Consent URL
    const fetchLoginConsentUrl = async () => {
        setLoadingLoginConsent(true);
        try {
            const response = await fetch(`/Azure/GetLoginAdminConsentURL?managedServiceId=${managedService.id}`);
            if (response.ok) {
                const url = await response.text();
                setLoginConsentUrl(url.replace(/^"|"$/g, ''));
            }
        } catch (error) {
            console.error('Error fetching login consent URL:', error);
        } finally {
            setLoadingLoginConsent(false);
        }
    };

    // Fetch Scadsys Events Admin Consent URL
    const fetchEventsConsentUrl = async () => {
        setLoadingEventsConsent(true);
        try {
            const response = await fetch(`/Azure/GetScadsysEventsAdminConsentURL?managedServiceId=${managedService.id}`);
            if (response.ok) {
                const url = await response.text();
                setEventsConsentUrl(url.replace(/^"|"$/g, ''));
            }
        } catch (error) {
            console.error('Error fetching events consent URL:', error);
        } finally {
            setLoadingEventsConsent(false);
        }
    };

    // Fetch Identity Users Admin Consent URL
    const fetchIdentityUsersConsentUrl = async () => {
        setLoadingIdentityUsersConsent(true);
        try {
            const response = await fetch(`/Azure/GetScadsysIdentityUsersAdminConsentURL?managedServiceId=${managedService.id}`);
            if (response.ok) {
                const url = await response.text();
                setIdentityUsersConsentUrl(url.replace(/^"|"$/g, ''));
            }
        } catch (error) {
            console.error('Error fetching identity users consent URL:', error);
        } finally {
            setLoadingIdentityUsersConsent(false);
        }
    };

    // Copy to clipboard function
    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            alert(`${label} URL copied to clipboard!`);
        }).catch(err => {
            console.error('Failed to copy:', err);
        });
    };

    return (
        <div>
            {managedService.type == 11 && <AppWebsiteEditor organizationId={managedService.organizationID} managedService={managedService} onDone={() => onChange && onChange()} />}
            {managedService.type == 1 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "graduation-cap"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">SASAMS Agent</h4>
                            <p className="text-sm text-gray-500">Student Administration System</p>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-blue-800 mb-3">Download the SASAMS agent to start your enrolment process and connect to the student administration system.</p>
                        <Button variant="primary" size="sm" onClick={() => window.open("https://www.scadco.co.za/sasams365/sasams-365.application", "_blank")}>
                            <FontAwesomeIcon icon={["fad", "download"]} className="mr-2" />
                            Download SASAMS Agent
                        </Button>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">Service Active</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">SASAMS integration is configured and ready for student data synchronization.</p>
                    </div>
                </>
            )}

            {managedService.type == 2 && <RenderScadsysInstance managedService={managedService} onChange={onChange} />}

            {managedService.type == 10 && <RenderScadsysAssetsInstance managedService={managedService} onChange={onChange} />}

            {managedService.type == 3 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "store"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">{managedService.configuration.name}</h4>
                            <p className="text-sm text-gray-500">Custom Store Integration</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                                <img
                                    className="rounded-lg border border-gray-300 shadow-sm"
                                    src={managedService.configuration.logoUri}
                                    alt={managedService.configuration.name}
                                    style={{ height: "80px", maxWidth: "120px" }}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                />
                            </div>
                            <div className="flex-1">
                                <p className="text-gray-700 mb-2">
                                    <span className="font-medium">Store Name:</span>
                                    <span className="ml-2">{managedService.configuration.name}</span>
                                </p>
                                <p className="text-gray-700">
                                    <span className="font-medium">Logo URL:</span>
                                    <span className="ml-2 font-mono text-sm break-all">{managedService.configuration.logoUri}</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">Store Active</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">Custom store integration is configured and ready to display in your organization.</p>
                    </div>
                </>
            )}

            {managedService.type == 4 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "fingerprint"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">{managedService.configuration.name}</h4>
                            <p className="text-sm text-gray-500">Biometric Authentication System</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="space-y-3">
                            <p className="text-gray-700 mb-2">
                                <span className="font-medium">Service Name:</span>
                                <span className="ml-2">{managedService.configuration.name}</span>
                            </p>
                            <p className="text-gray-700">
                                <span className="font-medium">Service URL:</span>
                                <span className="ml-2 font-mono text-sm">{managedService.configuration.url}</span>
                            </p>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "shield-check"]} className="text-yellow-600 mt-1" />
                            <div>
                                <h5 className="font-medium text-yellow-800 mb-1">Security Notice</h5>
                                <p className="text-yellow-700 text-sm">This biometric service handles sensitive authentication data. Ensure proper security measures are in place.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">Service Active</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">Biometric authentication is configured and ready for secure user identification.</p>
                    </div>
                </>
            )}

            {managedService.type == 5 && (
                <>
                    <h4 className="text-lg font-semibold mb-3">
                        Azure{" "}
                        {managedService.configuration.adminConsentGranted && (
                            <>
                                {/* @ts-ignore */}
                                <FontAwesomeIcon className="text-green-600" icon={["fad", "circle-check"]}></FontAwesomeIcon>
                            </>
                        )}
                    </h4>
                    <div className="space-y-3">
                        <p className="text-gray-700">
                            <span className="font-medium">Tenant ID:</span> {managedService.configuration.tenantID}
                        </p>
                        
                        {/* Consent URLs Section */}
                        <div className="bg-gray-50 rounded-md p-4 space-y-4">
                            {/* Login Admin Consent */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h5 className="font-medium text-gray-900">Login Admin Consent</h5>
                                        {managedService.configuration.adminConsentGranted && (
                                            <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                                <FontAwesomeIcon icon={["fad", "check-circle"]} />
                                                Granted {managedService.configuration.adminConsentGrantedTimestamp}
                                            </span>
                                        )}
                                    </div>
                                    <Button variant="primary" size="sm" onClick={fetchLoginConsentUrl} disabled={loadingLoginConsent}>
                                        {loadingLoginConsent ? (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "link"]} className="mr-2" />
                                                Get URL
                                            </>
                                        )}
                                    </Button>
                                </div>
                                {loginConsentUrl && (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={loginConsentUrl}
                                            readOnly
                                            className="flex-1 px-3 py-2 text-xs font-mono bg-gray-50 border border-gray-300 rounded"
                                        />
                                        <Button variant="secondary" size="sm" onClick={() => copyToClipboard(loginConsentUrl, "Login Consent")}>
                                            <FontAwesomeIcon icon={["fad", "copy"]} />
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={() => window.open(loginConsentUrl, "_blank")}>
                                            <FontAwesomeIcon icon={["fad", "external-link"]} />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Scadsys Events Admin Consent */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h5 className="font-medium text-gray-900">Scadsys Events Admin Consent</h5>
                                        {managedService.configuration.eventsAdminConsentGranted && (
                                            <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                                <FontAwesomeIcon icon={["fad", "check-circle"]} />
                                                Granted {managedService.configuration.eventsAdminConsentGrantedTimestamp}
                                            </span>
                                        )}
                                    </div>
                                    <Button variant="primary" size="sm" onClick={fetchEventsConsentUrl} disabled={loadingEventsConsent}>
                                        {loadingEventsConsent ? (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "link"]} className="mr-2" />
                                                Get URL
                                            </>
                                        )}
                                    </Button>
                                </div>
                                {eventsConsentUrl && (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={eventsConsentUrl}
                                            readOnly
                                            className="flex-1 px-3 py-2 text-xs font-mono bg-gray-50 border border-gray-300 rounded"
                                        />
                                        <Button variant="secondary" size="sm" onClick={() => copyToClipboard(eventsConsentUrl, "Events Consent")}>
                                            <FontAwesomeIcon icon={["fad", "copy"]} />
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={() => window.open(eventsConsentUrl, "_blank")}>
                                            <FontAwesomeIcon icon={["fad", "external-link"]} />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Identity Users Admin Consent */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h5 className="font-medium text-gray-900">Identity Users Admin Consent</h5>
                                        {managedService.configuration.identityUsersAdminConsentGranted && (
                                            <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                                <FontAwesomeIcon icon={["fad", "check-circle"]} />
                                                Granted {managedService.configuration.identityUsersAdminConsentGrantedTimestamp}
                                            </span>
                                        )}
                                    </div>
                                    <Button variant="primary" size="sm" onClick={fetchIdentityUsersConsentUrl} disabled={loadingIdentityUsersConsent}>
                                        {loadingIdentityUsersConsent ? (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "link"]} className="mr-2" />
                                                Get URL
                                            </>
                                        )}
                                    </Button>
                                </div>
                                {identityUsersConsentUrl && (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={identityUsersConsentUrl}
                                            readOnly
                                            className="flex-1 px-3 py-2 text-xs font-mono bg-gray-50 border border-gray-300 rounded"
                                        />
                                        <Button variant="secondary" size="sm" onClick={() => copyToClipboard(identityUsersConsentUrl, "Identity Users Consent")}>
                                            <FontAwesomeIcon icon={["fad", "copy"]} />
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={() => window.open(identityUsersConsentUrl, "_blank")}>
                                            <FontAwesomeIcon icon={["fad", "external-link"]} />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* License Management Section */}
                        {managedService.configuration.adminConsentGranted && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <AzureIndex organizationId={managedService.organizationID} managedService={managedService} />
                            </div>
                        )}
                    </div>
                </>
            )}

            {managedService.type == 6 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-500">Deprecated Service</h4>
                            <p className="text-sm text-gray-400">No longer supported</p>
                        </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "times-circle"]} className="text-red-600 mt-1" />
                            <div>
                                <h5 className="font-medium text-red-800 mb-1">Service Deprecated</h5>
                                <p className="text-red-700 text-sm">This managed service type is no longer supported and should be removed. Please contact support for migration options.</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {managedService.type == 7 && (
                <>
                    <h4 className="text-lg font-semibold mb-4">SendGrid</h4>

                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                        <Label>Send Test Mail To:</Label>
                        <Input type="text" value={emailTestAddress} onChange={(e) => setEmailTestAddress(e.target.value)} placeholder="Enter email address" className="mb-2" />
                        <Label>Test Mail Text:</Label>
                        <Input type="text" value={emailTestText} onChange={(e) => setEmailTestText(e.target.value)} placeholder="Enter test message" className="mb-3" />
                        <Button variant="primary" onClick={sendTestMail} size="sm">
                            Send Test Mail
                        </Button>
                    </div>

                    <FormGroup>
                        <Label>Default Template ID</Label>
                        <Input type="text" value={defaultTemplateID} onChange={(e) => setDefaultTemplateID(e.target.value)} onBlur={updateManagedService} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Login Link Template ID</Label>
                        <Input type="text" value={loginLinkTemplateID} onChange={(e) => setLoginLinkTemplateID(e.target.value)} onBlur={updateManagedService} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Transactional ASM Group ID</Label>
                        <Input type="number" value={transactionalAsmGroupID} onChange={(e) => setTransactionalAsmGroupID((e.target as HTMLInputElement).valueAsNumber)} onBlur={updateManagedService} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Vouchers ASM Group ID</Label>
                        <Input type="number" value={vouchersAsmGroupID} onChange={(e) => setVouchersAsmGroupID((e.target as HTMLInputElement).valueAsNumber)} onBlur={updateManagedService} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Marketing ASM Group ID</Label>
                        <Input type="number" value={marketingAsmGroupID} onChange={(e) => setMarketingAsmGroupID((e.target as HTMLInputElement).valueAsNumber)} onBlur={updateManagedService} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Forms ASM Group ID</Label>
                        <Input type="number" value={formsAsmGroupID} onChange={(e) => setFormsAsmGroupID((e.target as HTMLInputElement).valueAsNumber)} onBlur={updateManagedService} />
                    </FormGroup>
                </>
            )}

            {managedService.type == 8 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fab", "discord"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">Discord</h4>
                            <p className="text-sm text-gray-500">Community Integration</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="space-y-3">
                            <p className="text-gray-700 mb-2">
                                <span className="font-medium">Guild Name:</span>
                                <span className="ml-2">{managedService.configuration.guildName}</span>
                            </p>
                            <p className="text-gray-700">
                                <span className="font-medium">Guild ID:</span>
                                <span className="ml-2 font-mono text-sm">{managedService.configuration.guildID}</span>
                            </p>
                        </div>
                    </div>

                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "users"]} className="text-indigo-600 mt-1" />
                            <div>
                                <h5 className="font-medium text-indigo-800 mb-1">Community Features</h5>
                                <p className="text-indigo-700 text-sm">Discord integration enables community features and automated role management for your organization.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">Integration Active</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">Discord integration is configured and ready for community management.</p>
                    </div>
                </>
            )}

            {managedService.type == 9 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "database"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">Sage300</h4>
                            <p className="text-sm text-gray-500">ERP System Integration</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="space-y-3">
                            <p className="text-gray-700 mb-2">
                                <span className="font-medium">Server URL:</span>
                                <span className="ml-2 font-mono text-sm">{managedService.configuration.url}</span>
                            </p>
                            <p className="text-gray-700 mb-2">
                                <span className="font-medium">Username:</span>
                                <span className="ml-2">{managedService.configuration.username}</span>
                            </p>
                            <p className="text-gray-700">
                                <span className="font-medium">Password:</span>
                                <span className="ml-2 text-gray-500">••••••••</span>
                            </p>
                        </div>
                    </div>

                    {managedService.configuration.filter && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <h5 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                                <FontAwesomeIcon icon={["fad", "filter"]} className="text-blue-600" />
                                Filter Configuration
                            </h5>
                            {(() => {
                                try {
                                    const filter = JSON.parse(managedService.configuration.filter);
                                    if (filter.field && filter.value && filter.operator) {
                                        return (
                                            <div className="space-y-2">
                                                <div className="bg-blue-100 rounded-lg p-3">
                                                    <p className="text-blue-800 font-medium">Basic Filter</p>
                                                    <p className="text-blue-700 text-sm">
                                                        <span className="font-medium">{filter.field}</span> {filter.operator}{" "}
                                                        <span className="font-mono bg-blue-200 px-2 py-1 rounded">{filter.value}</span>
                                                    </p>
                                                </div>
                                                <details className="text-blue-700">
                                                    <summary className="cursor-pointer text-xs hover:text-blue-800 select-none">Show raw JSON</summary>
                                                    <pre className="text-xs mt-2 bg-blue-100 p-2 rounded overflow-x-auto">{JSON.stringify(filter, null, 2)}</pre>
                                                </details>
                                            </div>
                                        );
                                    }
                                } catch (e) {
                                    // Fall back to raw JSON display for complex filters
                                }
                                return (
                                    <div>
                                        <p className="text-blue-800 font-medium mb-2">Advanced Filter</p>
                                        <pre className="text-xs text-blue-700 bg-blue-100 p-2 rounded overflow-x-auto">{managedService.configuration.filter}</pre>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">Integration Active</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">Sage300 ERP integration is configured and ready to process financial and operational data.</p>
                    </div>
                </>
            )}

            {managedService.type == 0 && (
                <>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={["fad", "link"]} className="text-white text-lg" />
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">Custom Link</h4>
                            <p className="text-sm text-gray-500">External Resource Integration</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                                <img
                                    src={managedService.configuration.imageURI}
                                    width="64"
                                    className="rounded-lg border border-gray-300 shadow-sm"
                                    alt={managedService.configuration.name}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                />
                            </div>
                            <div className="flex-1">
                                <p className="text-gray-700 mb-2">
                                    <span className="font-medium">Link Name:</span>
                                    <span className="ml-2">{managedService.configuration.name}</span>
                                </p>
                                <p className="text-gray-700 mb-3">
                                    <span className="font-medium">Target URL:</span>
                                    <span className="ml-2 font-mono text-sm break-all">{managedService.configuration.link}</span>
                                </p>
                                <Button variant="secondary" size="sm" onClick={() => window.open(managedService.configuration.link, "_blank")}>
                                    <FontAwesomeIcon icon={["fad", "external-link"]} className="mr-2" />
                                    Open Link
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h5 className="font-medium text-blue-800 mb-3">Access Control</h5>
                        <FormGroup>
                            <Label htmlFor="limitTo">Limit Access To</Label>
                            <Input id="limitTo" type="text" value={limitTo} onChange={(e) => setLimitTo(e.target.value)} onBlur={updateManagedService} placeholder="e.g., 0,1,2" />
                            <p className="text-blue-600 text-xs mt-1">Comma separated list: 0=staff, 1=student, 2=parent, 4=guest (leave empty for all users)</p>
                        </FormGroup>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                        <h5 className="font-medium text-purple-800 mb-3">Display Options</h5>
                        <FormGroup>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={openInIframe}
                                    onChange={(e) => setOpenInIframe(e.target.checked)}
                                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-gray-700">Open in iframe</span>
                            </label>
                            <p className="text-purple-600 text-xs mt-1 ml-7">When enabled, the link will open in an embedded iframe within the application instead of opening in a new tab</p>
                        </FormGroup>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">Link Active</span>
                        </div>
                        <p className="text-green-700 text-sm mt-1">Custom link is configured and ready to be displayed to authorized users.</p>
                    </div>
                </>
            )}

            <div className="flex justify-end w-full mt-6">
                <Button variant="danger" onClick={startDeleteManagedService}>
                    Delete ManagedService
                </Button>
            </div>
            <Modal isOpen={showDeleteModal} onClose={toggleDeleteModal} title="Delete ManagedService" icon={["fad", "trash"]} iconColor="text-red-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700">Are you sure you want to delete this managed service? This action cannot be undone.</p>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={toggleDeleteModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={deleteManagedService}>
                        Delete
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default ManagedServiceInstance;
